/**
 * useWorkspaceMembers Hook - Paginated Member Management
 *
 * Custom hook for workspace member list with pagination, search, and role management.
 *
 * @module hooks/useWorkspaceMembers
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useDebounce } from './useDebounce';
import { apiClient } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import {
  WORKSPACE_DEFAULT_PAGE_SIZE,
  WORKSPACE_SEARCH_DEBOUNCE_MS,
} from '../constants/workspace';
import type { WorkspaceMember, WorkspaceRole } from '../types/workspace';
import type {
  GetWorkspaceMembersResponse,
  UpdateMemberRoleResponse,
  RemoveWorkspaceMemberResponse,
} from '../types/api/responses';

interface UseWorkspaceMembersParams {
  workspaceId: string | null;
  pageSize?: number;
  autoFetch?: boolean;
}

interface UseWorkspaceMembersReturn {
  // State
  members: WorkspaceMember[];
  search: string;
  roleFilter: WorkspaceRole | null;
  totalCount: number;
  isLoading: boolean;
  hasLoaded: boolean;
  hasNextPage: boolean;
  error: string | null;

  // Actions
  setSearch: (search: string) => void;
  setRoleFilter: (role: WorkspaceRole | null) => void;
  loadMore: () => void;
  updateMemberRole: (
    userId: string,
    newRole: WorkspaceRole
  ) => Promise<boolean>;
  removeMember: (userId: string) => Promise<boolean>;
  refresh: () => void;
}

/**
 * Hook for paginated workspace member management
 */
export function useWorkspaceMembers({
  workspaceId,
  pageSize = WORKSPACE_DEFAULT_PAGE_SIZE,
  autoFetch = true,
}: UseWorkspaceMembersParams): UseWorkspaceMembersReturn {
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<WorkspaceRole | null>(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { debouncedValue: debouncedSearch } = useDebounce(
    search,
    WORKSPACE_SEARCH_DEBOUNCE_MS
  );
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchMembers = useCallback(
    async (pageNum: number, append = false) => {
      if (!workspaceId) {
        setMembers([]);
        return;
      }

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          page: pageNum.toString(),
          pageSize: pageSize.toString(),
        });

        if (debouncedSearch) {
          params.set('search', debouncedSearch);
        }
        if (roleFilter) {
          params.set('role', roleFilter);
        }

        const response = await apiClient.get<GetWorkspaceMembersResponse>(
          `${API_ENDPOINTS.WORKSPACES.MEMBERS(workspaceId)}?${params}`,
          { signal: abortControllerRef.current.signal }
        );

        const newMembers = response.data.items;

        setMembers(append ? (prev) => [...prev, ...newMembers] : newMembers);
        setTotalCount(response.data.totalCount);
        setHasNextPage(response.data.hasNextPage);
        setPage(pageNum);
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          const message = err.message || 'Failed to fetch members';
          setError(message);
          console.error('Failed to fetch members:', err);
        }
      } finally {
        setHasLoaded(true);
        setIsLoading(false);
      }
    },
    [workspaceId, pageSize, debouncedSearch, roleFilter]
  );

  const loadMore = useCallback(() => {
    if (!isLoading && hasNextPage) {
      fetchMembers(page + 1, true);
    }
  }, [isLoading, hasNextPage, page, fetchMembers]);

  const updateMemberRole = useCallback(
    async (userId: string, newRole: WorkspaceRole): Promise<boolean> => {
      if (!workspaceId) return false;

      try {
        await apiClient.put<UpdateMemberRoleResponse>(
          API_ENDPOINTS.WORKSPACES.MEMBER_ROLE(workspaceId, userId),
          { newRole }
        );

        // Update local state
        setMembers((prev) =>
          prev.map((member) =>
            member.userId === userId ? { ...member, role: newRole } : member
          )
        );

        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to update role';
        setError(message);
        return false;
      }
    },
    [workspaceId]
  );

  const removeMember = useCallback(
    async (userId: string): Promise<boolean> => {
      if (!workspaceId) return false;

      try {
        await apiClient.delete<RemoveWorkspaceMemberResponse>(
          API_ENDPOINTS.WORKSPACES.REMOVE_MEMBER(workspaceId, userId)
        );

        // Update local state
        setMembers((prev) => prev.filter((member) => member.userId !== userId));
        setTotalCount((prev) => prev - 1);

        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to remove member';
        setError(message);
        return false;
      }
    },
    [workspaceId]
  );

  const refresh = useCallback(() => {
    setPage(1);
    fetchMembers(1);
  }, [fetchMembers]);

  useEffect(() => {
    if (autoFetch && workspaceId) {
      fetchMembers(1);
    }
  }, [autoFetch, workspaceId, fetchMembers]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    setMembers([]);
    setSearch('');
    setRoleFilter(null);
    setPage(1);
    setTotalCount(0);
    setHasNextPage(false);
    setHasLoaded(false);
    setError(null);
  }, [workspaceId]);

  return {
    // State
    members,
    search,
    roleFilter,
    totalCount,
    isLoading,
    hasLoaded,
    hasNextPage,
    error,

    // Actions
    setSearch,
    setRoleFilter,
    loadMore,
    updateMemberRole,
    removeMember,
    refresh,
  };
}
